import { proposalCollectionItemLabel } from "work/proposals/negotiation/proposal-change-descriptors";
import { describeProposalTimelineChange } from "work/proposals/negotiation/proposal-change-descriptors";
import {
  proposalChangeActorLabel,
  proposalChangeFieldLabel,
  proposalChangeValueText,
  proposalResolutionDecisionAction,
} from "work/proposals/negotiation/proposal-change-description";
import {
  isProposalFeeScheduleAspect,
  isProposalFeeScheduleTeamMemberProperty,
  proposalFeeScheduleAspectText,
  proposalFeeScheduleCategoriesPath,
  proposalFeeScheduleColumnLabel,
  proposalFeeScheduleLeafPayload,
  proposalFeeScheduleTeamMemberText,
  proposalFeeScheduleUnknownColumnText,
  readProposalFeeScheduleAspectValue,
  readProposalFeeScheduleCategoryValues,
  type ProposalFeeScheduleAspect,
  type ProposalFeeScheduleCategoryValues,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  proposalCollectionItemConflictKeyAspect,
  proposalCollectionItemUnreadableValueText,
  proposalCollectionPathFromConflictKey,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import {
  isProposalFeeScheduleCategoryPath,
  summarizeProposalFeeScheduleChanges,
  type ProposalFeeScheduleCategoryAction,
  type ProposalFeeScheduleChangeInput,
  type ProposalFeeScheduleChangeSummary,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";
import type {
  ProposalActorRole,
  ProposalNegotiationEventKind,
  ProposalNegotiationParty,
  ProposalTimelineEntry,
} from "work/proposals/negotiation/proposal-collaboration-contract";

/**
 * A comment as consumed by the merged ("All") timeline view. Comments are Messaging-native (a Message
 * in a topic-filtered Forum), so the panel maps the Forum/Message comment store into this neutral shape
 * rather than depending on any bespoke comment REST contract.
 */
export type ProposalTimelineComment = {
  messageId: string;
  path?: string | null;
  sourceParty?: ProposalNegotiationParty | null;
  authorUserId?: string | null;
  authorLegalEntityId?: string | null;
  /**
   * When the comment took its place in the conversation, for ordering it among the changes. Never
   * absent: a comment whose write is still on the wire carries neither moment yet, and its caller dates
   * it now rather than leaving the list to guess (see `Comment.enteredConversationAt`).
   */
  createdDate: string;
  publishedDate?: string | null;
  content: string;
};

/**
 * Names the collection item a timeline entry is about, given its path and item id.
 *
 * Optional, and only ever a first choice: an entry's own value already names most items. It exists for
 * the ones whose value cannot — a document, whose file name is display metadata the negotiation ledger
 * deliberately strips — and is supplied by whoever has the proposal projection to hand, which the panel
 * itself does not.
 */
export type ProposalTimelineItemLabelResolver = (path: string, itemId: string) => string | undefined;

/**
 * Hands back a collection item's whole current value, for a surface that needs more of the item than
 * its name. The fee schedule is why: a history row draws the entire category with only the columns that
 * moved blue-lined (#4845), and the columns that did not move are not in the stream's leaves at all —
 * they are the state those leaves happened to. Optional and, like the label resolver, supplied by
 * whoever has the proposal projection to hand.
 */
export type ProposalTimelineItemValueResolver = (path: string, itemId: string) => unknown;

/**
 * Names a person from their user id, for the one fee-schedule column whose value is a person: the
 * assigned team member (#4949). Optional, like the other resolvers, and supplied by whoever has the
 * Individuals store to hand; without it the column reads the table's own stand-in for an unnamed
 * member, never an id.
 */
export type ProposalTimelineUserNameResolver = (userId: string) => string | undefined;

export const proposalTimelineColorTokens = ["blue", "orange", "green", "purple", "teal", "pink"] as const;

export type ProposalTimelineColorToken = (typeof proposalTimelineColorTokens)[number];

export type ProposalTimelineEntryView = {
  sequence: number;
  /** Stable React key. Field changes key by stream sequence; comments key by message id. */
  key: string;
  commandId?: string;
  conflictKey?: string | null;
  kind: ProposalNegotiationEventKind;
  label: string;
  /** Human-readable field name (e.g. "Response Due By") derived from the semantic path. */
  fieldLabel?: string;
  /**
   * Which item within a collection field the entry is about — a fee-schedule category's name, a
   * document's file name. Without it every change to a collection reads as its bare field name, so
   * three separate fee-schedule categories produced three identical "added Fee Schedule" rows with
   * nothing to tell them apart. Undefined for scalar fields, which are their own subject.
   */
  itemLabel?: string;
  /** Party-free verb for the change ("updated", "added", "commented", …). */
  actionVerb: string;
  /** True when the entry is a team-roster or team-leader change rendered in member-centric language. */
  isTeamChange: boolean;
  /** The discriminated team change (member/leader add/remove/change), rendered descriptively by the panel. */
  teamChange?: ProposalTimelineTeamChange;
  /** True when the entry is a reviewer change rendered in reviewer-centric language. */
  isReviewerChange: boolean;
  /**
   * The discriminated reviewer change (added/removed/access/message), rendered descriptively by the
   * panel. Without it every one of them read as "{author} changed reviewer access Reviewers", naming
   * neither the reviewer it was about nor what their access became.
   */
  reviewerChange?: ProposalTimelineReviewerChange;
  path?: string | null;
  /** Stable collection-item identity used for grouping; labels are display-only and need not be unique. */
  itemId?: string | null;
  sourceParty?: ProposalNegotiationParty | null;
  actorRole?: ProposalActorRole | null;
  actorUserId?: string | null;
  actorLegalEntityId?: string | null;
  createdDate: string;
  valueSummary?: string;
  /** Scalar previous value for display (undefined when missing or structured JSON). */
  previousDisplayValue?: string;
  /** Scalar new value for display (undefined when missing or structured JSON). */
  nextDisplayValue?: string;
  /** Individual semantic leaves collapsed into one same-command collection-item history row. */
  changeDetails?: ProposalTimelineChangeDetail[];
  /**
   * A whole save's worth of fee-schedule edits, collapsed into one description of the schedule. Set
   * only on the grouped fee-schedule row; see `proposal-fee-schedule-change-summary.ts`.
   */
  feeScheduleChange?: ProposalFeeScheduleChangeSummary;
  /**
   * This entry's own fee-schedule leaf — which column of which category it moved. Present on every
   * fee-schedule entry, and consumed by the grouping below to build `feeScheduleChange`; see
   * {@link ProposalFeeScheduleTimelineLeaf}.
   */
  feeScheduleLeaf?: ProposalFeeScheduleTimelineLeaf;
  /**
   * The scalar value itself, unformatted — what a revert restores, as opposed to what a reader sees.
   *
   * For a date these are not the same string: `nextDisplayValue` is a local calendar date ("28 September,
   * 2026") while the stored value is an ISO datetime. Feeding the rendering back through the field's
   * setter produced an Invalid Date and a failed proposal validation, so a revert reads this instead.
   * Undefined whenever the value is absent or structured, exactly like `nextDisplayValue`.
   */
  nextValue?: string | number | boolean;
  /** True when the value is an object/array, so callers avoid rendering raw JSON. */
  valueIsStructured: boolean;
  sourceKey: string;
  colorToken: ProposalTimelineColorToken;
  isComment: boolean;
  isContentChange: boolean;
  isPublishedAlternative: boolean;
  /**
   * The value this field started with when the proposal was created, rather than an edit somebody
   * made. Only a field's own history carries these — the proposal-wide feed drops them server-side —
   * and it keeps them so the first real edit to a field has a previous value to revert to. Rendered
   * as the original value, never as "{author} updated {field}": nobody performed it, and attributing
   * it to the creator reported edits to fields they had not touched.
   */
  isCreationBaseline: boolean;
};

export type ProposalTimelineChangeDetail = {
  fieldLabel: string;
  previousDisplayValue?: string;
  nextDisplayValue?: string;
};

/**
 * Explicit server metadata wins; the role fallback only supports timeline payloads from an older
 * deployment, before later automatic events could also carry the System role.
 */
export function proposalTimelineEntryIsCreationBaseline(
  entry: Pick<ProposalTimelineEntry, "actorRole" | "isCreationBaseline">,
): boolean {
  return entry.isCreationBaseline ?? entry.actorRole === "System";
}

export function buildProposalTimelineView(
  entries: readonly ProposalTimelineEntry[],
  resolveItemLabel?: ProposalTimelineItemLabelResolver,
  resolveItemValue?: ProposalTimelineItemValueResolver,
  resolveUserName?: ProposalTimelineUserNameResolver,
): ProposalTimelineEntryView[] {
  const colorTokensBySource = assignProposalTimelineColorTokens(entries);
  const views = entries
    .slice()
    .sort((left, right) => left.sequence - right.sequence)
    .map((entry) =>
      proposalTimelineChangeEntryToView(
        entry,
        colorTokensBySource[proposalTimelineSourceKey(entry)] ?? "blue",
        resolveItemLabel,
      ),
    );
  return groupFeeScheduleCategoryChanges(dedupeConsecutiveTeamChanges(views), resolveItemValue, resolveUserName);
}

/**
 * One fee-schedule edit is persisted as one event per semantic leaf so conflicts remain precise. In a
 * human history that whole edit is one action — across categories, not just within one.
 *
 * Grouping used to stop at the category, so a save that renamed one category, repriced another and
 * added a third produced three rows the reader had to reassemble. It now groups by the command (a
 * save or submission) and its actor, and the row it produces describes the entirety of what that save
 * did to the schedule. A creation baseline groups separately, which is what makes the schedule the
 * proposal started with a single "Original value for Fee Schedule" entry rather than one per leaf.
 *
 * Older payloads without command identity remain ungrouped rather than guessing.
 */
function groupFeeScheduleCategoryChanges(
  views: readonly ProposalTimelineEntryView[],
  resolveItemValue?: ProposalTimelineItemValueResolver,
  resolveUserName?: ProposalTimelineUserNameResolver,
): ProposalTimelineEntryView[] {
  const grouped = new Map<string, FeeScheduleChangeGroup>();
  const result: ProposalTimelineEntryView[] = [];

  for (const view of views) {
    // `feeScheduleLeaf` is the one thing that decides whether this is a fee-schedule category change,
    // and it already accepts an entry whose path is absent by reading the collection out of its
    // conflict key. Re-testing `view.path` here would have overruled it and dropped exactly the
    // pathless entries the leaf was taught to recognise.
    const canGroup =
      Boolean(view.itemId && view.itemLabel && view.conflictKey) &&
      // A baseline is the schedule the proposal started with, and belongs in one entry whether or not
      // the payload carries the command that seeded it.
      Boolean(view.commandId || view.isCreationBaseline);
    if (!canGroup || !view.feeScheduleLeaf) {
      result.push(view);
      continue;
    }

    // The baseline and the edits made in the same command are two different statements about the
    // schedule -- "this is what it started as" and "this is what changed" -- so they never merge.
    const groupKey = [
      view.isCreationBaseline ? "baseline" : (view.commandId ?? ""),
      view.sourceKey,
      view.isCreationBaseline ? "baseline" : "change",
    ].join("|");
    const existing = grouped.get(groupKey);
    if (!existing) {
      const first: ProposalTimelineEntryView = {
        ...view,
        key: `fee-schedule-${groupKey}`,
        // The row is about the schedule, not about one of its categories, so it is labelled with the
        // field a reader would go looking under.
        fieldLabel: proposalChangeFieldLabel("/feeSchedule"),
        // The row is about the schedule as a whole; naming one of its categories in the headline would
        // claim the save touched only that one.
        itemLabel: undefined,
        actionVerb: "updated",
      };
      grouped.set(groupKey, { view: first, leaves: [view.feeScheduleLeaf] });
      result.push(first);
      continue;
    }

    existing.view.sequence = Math.max(existing.view.sequence, view.sequence);
    existing.view.createdDate = view.createdDate;
    existing.leaves.push(view.feeScheduleLeaf);
  }

  const states = resolveFeeScheduleCategoryStates(grouped, resolveItemValue);
  for (const group of grouped.values()) {
    group.view.feeScheduleChange = summarizeProposalFeeScheduleChanges(
      feeScheduleChangeInputs(group, states, resolveUserName),
    );
  }

  return result;
}

type FeeScheduleChangeGroup = {
  view: ProposalTimelineEntryView;
  leaves: ProposalFeeScheduleTimelineLeaf[];
};

/**
 * A category's columns on both sides of each grouped row, keyed by row then category.
 *
 * Both, not just `after`: a rate and its currency can move in the same command, and the rate that was
 * has to read in the currency it was in. Formatting `$100 -> EUR 125` from the after-state alone
 * produced `EUR 100 -> EUR 125`, a price the schedule never carried.
 */
type FeeScheduleCategoryRowState = {
  before: ProposalFeeScheduleCategoryValues;
  after: ProposalFeeScheduleCategoryValues;
};
type FeeScheduleCategoryStates = Map<ProposalTimelineEntryView, Map<string, FeeScheduleCategoryRowState>>;

/**
 * Reconstructs what each category looked like at every point in the history, so a row can draw the
 * whole category rather than only the columns that moved.
 *
 * The columns a save did NOT touch are not in the stream -- they are the state those events happened
 * to -- so they have to be derived. Deriving them forwards is not possible here: the proposal-wide feed
 * drops the creation baseline server-side (see `ProposalNegotiationConversation.ForProposal`), so a
 * category that existed before anybody edited it has no event stating its original columns. Deriving
 * them BACKWARDS is, and exactly: the current category is known from the projection, and every event
 * carries what its column held before it (`expectedValue`), so walking newest to oldest and undoing
 * each change reproduces the schedule as of every row without needing a baseline at all.
 *
 * A category that no longer exists needs no projection -- its removal event carries the whole item --
 * and one added during the negotiation carries the whole item on its add. Rolling back past an add
 * empties the category, because before it was added there was nothing there.
 */
function resolveFeeScheduleCategoryStates(
  groups: ReadonlyMap<string, FeeScheduleChangeGroup>,
  resolveItemValue?: ProposalTimelineItemValueResolver,
): FeeScheduleCategoryStates {
  const states: FeeScheduleCategoryStates = new Map();
  const rolledBack = new Map<string, ProposalFeeScheduleCategoryValues>();

  for (const group of [...groups.values()].reverse()) {
    const afterGroup = new Map<string, ProposalFeeScheduleCategoryValues>();
    for (const leaf of group.leaves) {
      const known =
        afterGroup.get(leaf.itemId) ??
        rolledBack.get(leaf.itemId) ??
        readProposalFeeScheduleCategoryValues(resolveItemValue?.(proposalFeeScheduleCategoriesPath, leaf.itemId));
      // A whole-item payload (an add's value, a removal's expected value) states every column at once.
      afterGroup.set(leaf.itemId, { ...known, ...leaf.itemValues });
    }
    // Snapshot before rolling back: this is the state the row is about, not the one before it.
    const after = new Map([...afterGroup].map(([itemId, values]) => [itemId, { ...values }]));

    for (const leaf of group.leaves) {
      const before = afterGroup.get(leaf.itemId);
      if (!before) continue;
      if (leaf.action === "added") {
        // Before the add there was no category, so no column of it held anything.
        afterGroup.set(leaf.itemId, {});
        continue;
      }
      if (leaf.aspect) before[leaf.aspect] = leaf.previousExists ? leaf.previousValue : undefined;
    }

    // ...and again once the group is undone, which is the state its own previous values belong to.
    states.set(
      group.view,
      new Map([...after].map(([itemId, values]) => [itemId, { after: values, before: { ...afterGroup.get(itemId) } }])),
    );
    for (const [itemId, before] of afterGroup) rolledBack.set(itemId, before);
  }

  return states;
}

function feeScheduleChangeInputs(
  group: FeeScheduleChangeGroup,
  states: FeeScheduleCategoryStates,
  resolveUserName?: ProposalTimelineUserNameResolver,
): ProposalFeeScheduleChangeInput[] {
  const byItemId = states.get(group.view);
  return group.leaves.map((leaf) => {
    const rowState = byItemId?.get(leaf.itemId);
    const categoryValues = rowState?.after ?? {};
    // Each half of a transition reads in the category as it stood on that side of the command. The rate
    // reads as money, which takes the currency column beside it (see `proposalFeeScheduleAspectText`),
    // and that column can move in the very same command -- so the rate that WAS reads in the currency
    // it was in, and the rate that IS in the one it is in.
    const previousValues = rowState?.before ?? {};
    // The assigned member is a person, and reads as one: who it was, who it is now, or "Unassigned" --
    // the same transition every other column draws (#4949). A member the directory cannot name reads
    // as the table's own stand-in rather than as an id; the server has already stripped the column for
    // a viewer who may not see it, so an entry that reaches here is one the viewer may read.
    if (isProposalFeeScheduleTeamMemberProperty(leaf.property)) {
      const memberText = (exists: boolean, value: unknown) =>
        exists ? (proposalFeeScheduleTeamMemberText(value, resolveUserName) ?? proposalCollectionItemUnreadableValueText) : undefined;
      return {
        itemId: leaf.itemId,
        itemLabel: leaf.itemLabel,
        fieldLabel: proposalFeeScheduleColumnLabel(leaf.property),
        action: leaf.action,
        previousText: memberText(leaf.previousExists, leaf.previousValue),
        nextText: memberText(leaf.nextExists, leaf.nextValue),
        categoryValues,
      };
    }
    // A column with no rendering rule here still changed, and the row has to say so. It reads what
    // reads and otherwise reports the change itself, on whichever side of the transition exists -- one
    // side only, because "(changed) (changed)" describes nothing twice.
    if (leaf.property && !leaf.aspect) {
      const nextText = proposalFeeScheduleUnknownColumnText(leaf.nextExists, leaf.nextValue);
      return {
        itemId: leaf.itemId,
        itemLabel: leaf.itemLabel,
        fieldLabel: proposalFeeScheduleColumnLabel(leaf.property),
        action: leaf.action,
        previousText: nextText ? undefined : proposalFeeScheduleUnknownColumnText(leaf.previousExists, leaf.previousValue),
        nextText,
        categoryValues,
      };
    }
    return {
      itemId: leaf.itemId,
      itemLabel: leaf.itemLabel,
      aspect: leaf.aspect,
      action: leaf.action,
      previousText:
        leaf.aspect && leaf.previousExists
          ? proposalFeeScheduleAspectText(leaf.aspect, leaf.previousValue, previousValues)
          : undefined,
      nextText:
        leaf.aspect && leaf.nextExists
          ? proposalFeeScheduleAspectText(leaf.aspect, leaf.nextValue, categoryValues)
          : undefined,
      categoryValues,
    };
  });
}

/**
 * This entry's fee-schedule leaf: which column it changed, what that column held on either side, and
 * (for an add or a removal) the whole category the payload carries. Kept on the view so grouping can
 * fold the schedule's state without going back to the raw entries.
 */
export type ProposalFeeScheduleTimelineLeaf = {
  itemId: string;
  itemLabel?: string;
  action: ProposalFeeScheduleCategoryAction;
  /** Absent when the event is about the category itself rather than one of its columns. */
  aspect?: ProposalFeeScheduleAspect;
  /**
   * The column the conflict key names, canonical or not — `teamMember` as readily as `feeRate`.
   *
   * `aspect` is the subset this vocabulary has a rendering rule for; `property` is whatever the stream
   * actually said. Keeping both is what lets a column the build does not know still appear as a column
   * that changed instead of vanishing from the row.
   */
  property?: string;
  previousExists: boolean;
  previousValue?: unknown;
  nextExists: boolean;
  nextValue?: unknown;
  itemValues: ProposalFeeScheduleCategoryValues;
};

function proposalFeeScheduleTimelineLeaf(
  entry: ProposalTimelineEntry,
  itemLabel?: string,
): ProposalFeeScheduleTimelineLeaf | undefined {
  if (!entry.itemId) return undefined;
  // An entry's path is optional on the wire, and recognising a fee-schedule change by it alone meant a
  // pathless one was not recognised at all: it never became a schedule row, so it drew as a bare
  // structured value instead of as the category's column that it is. The conflict key was built from
  // the path, so it can say which collection the entry belongs to when the entry does not.
  const categoryPath = isProposalFeeScheduleCategoryPath(entry.path)
    ? entry.path
    : proposalCollectionPathFromConflictKey(entry.conflictKey, entry.itemId);
  if (!isProposalFeeScheduleCategoryPath(categoryPath)) return undefined;

  // The column the key names, whatever it is. Read against the item rather than by position, so a
  // whole-item key (`...:{itemId}`) answers "no column" instead of mistaking the id for one.
  const property = proposalCollectionItemConflictKeyAspect(categoryPath, entry.itemId, entry.conflictKey);
  const aspect = isProposalFeeScheduleAspect(property) ? property : undefined;
  return {
    itemId: entry.itemId,
    itemLabel,
    action: feeScheduleCategoryAction(entry.kind),
    aspect,
    property,
    previousExists: entry.expectedValueExists,
    previousValue: property ? readProposalFeeScheduleAspectValue(entry.expectedValue, property) : entry.expectedValue,
    nextExists: entry.valueExists,
    nextValue: property ? readProposalFeeScheduleAspectValue(entry.value, property) : entry.value,
    // Only a whole-item payload states more than one column; a leaf payload states exactly the one
    // `property` names, and claiming the rest absent would erase them from the row.
    itemValues: property
      ? {}
      : readProposalFeeScheduleCategoryValues(entry.valueExists ? entry.value : entry.expectedValue),
  };
}

function feeScheduleCategoryAction(kind: ProposalNegotiationEventKind): ProposalFeeScheduleCategoryAction {
  if (kind === "CollectionItemAdded") return "added";
  if (kind === "CollectionItemRemoved") return "removed";
  return "updated";
}

/**
 * Collapses a run of identical consecutive team entries into one. Picking a team from a template
 * before the first save can emit the same roster change twice; without this the history would show
 * "{member} added to team" back to back. Only exact-duplicate adjacent team entries collapse — any
 * change in member, path, kind, or party breaks the run — so genuinely distinct edits are preserved.
 */
function dedupeConsecutiveTeamChanges(views: readonly ProposalTimelineEntryView[]): ProposalTimelineEntryView[] {
  return views.filter((view, index) => {
    if (index === 0 || !view.isTeamChange) return true;
    const previous = views[index - 1];
    if (!previous.isTeamChange) return true;
    return teamChangeDedupeSignature(view) !== teamChangeDedupeSignature(previous);
  });
}

function teamChangeDedupeSignature(view: ProposalTimelineEntryView): string {
  const memberUserId = view.teamChange?.nextUserId ?? view.teamChange?.previousUserId ?? "";
  return [view.path ?? "", view.kind, memberUserId, view.sourceParty ?? ""].join("|");
}

/**
 * Builds the "All" view: the Work field-change timeline interleaved with the Messaging comments,
 * ordered by timestamp. Comments are Messaging-native and no longer ride the Work stream, so any
 * stray comment-kind Work entry is dropped and the real comments are folded in from the comment feed.
 */
export function buildMergedProposalTimelineView(
  entries: readonly ProposalTimelineEntry[],
  comments: readonly ProposalTimelineComment[],
  resolveItemLabel?: ProposalTimelineItemLabelResolver,
  resolveItemValue?: ProposalTimelineItemValueResolver,
  resolveUserName?: ProposalTimelineUserNameResolver,
): ProposalTimelineEntryView[] {
  const changeViews = entries
    .filter((entry) => !proposalTimelineEntryKindIsComment(entry.kind))
    .map((entry) => proposalTimelineChangeEntryToView(entry, "blue", resolveItemLabel));
  const commentViews = comments.map((comment) => proposalCommentToTimelineView(comment, "blue"));
  const merged = [...changeViews, ...commentViews].sort(
    (left, right) => timelineViewTimestamp(left) - timelineViewTimestamp(right) || left.key.localeCompare(right.key),
  );
  const colorTokensBySource = assignMergedTimelineColorTokens(merged);
  return groupFeeScheduleCategoryChanges(
    merged.map((view) => ({
      ...view,
      colorToken: colorTokensBySource[view.sourceKey] ?? "blue",
    })),
    resolveItemValue,
    resolveUserName,
  );
}

function proposalTimelineChangeEntryToView(
  entry: ProposalTimelineEntry,
  colorToken: ProposalTimelineColorToken,
  resolveItemLabel?: ProposalTimelineItemLabelResolver,
): ProposalTimelineEntryView {
  const teamChange = proposalTimelineTeamChange(entry);
  const reviewerChange = proposalTimelineReviewerChange(entry);
  const descriptor = describeProposalTimelineChange(entry, { resolveItemLabel });
  // The resolver first, the entry's own value second. A collection item is normally nameable from
  // the value the entry carries — a fee category keeps its `name` through canonicalization — but a
  // DOCUMENT does not: its name lives in the display metadata the ledger deliberately strips, so
  // every policy, conflicts and contract document produced an unnamed row. Three documents added to
  // one proposal rendered as three identical "added Client Policies", which is the same failure the
  // item label was introduced to fix for fee categories.
  const itemLabel =
    descriptor.itemLabel ?? proposalCollectionItemLabel(entry.valueExists ? entry.value : entry.expectedValue);
  return {
    sequence: entry.sequence,
    commandId: entry.commandId,
    conflictKey: entry.conflictKey,
    key: `change-${entry.sequence}`,
    kind: entry.kind,
    label: describeProposalTimelineEntry(entry),
    fieldLabel: descriptor.fieldLabel,
    itemLabel,
    actionVerb: proposalTimelineActionVerb(entry.kind, entry.valueExists ? entry.value : undefined),
    feeScheduleLeaf: proposalFeeScheduleTimelineLeaf(entry, itemLabel),
    isTeamChange: Boolean(teamChange),
    teamChange,
    isReviewerChange: Boolean(reviewerChange),
    reviewerChange,
    path: entry.path,
    itemId: entry.itemId,
    sourceParty: entry.sourceParty,
    actorUserId: entry.actorUserId,
    actorRole: entry.actorRole,
    actorLegalEntityId: entry.actorLegalEntityId,
    createdDate: entry.createdDate,
    valueSummary: summarizeProposalTimelineValue(entry),
    previousDisplayValue: proposalTimelineDisplayValue(entry.expectedValueExists, entry.expectedValue, entry.path),
    nextDisplayValue: proposalTimelineDisplayValue(entry.valueExists, entry.value, entry.path),
    nextValue: proposalTimelineScalarValue(entry.valueExists, entry.value),
    valueIsStructured: proposalTimelineValueIsStructured(entry.valueExists, entry.value),
    sourceKey: proposalTimelineSourceKey(entry),
    colorToken,
    isComment: proposalTimelineEntryKindIsComment(entry.kind),
    isContentChange: proposalTimelineEntryIsContentChange(entry.kind),
    isPublishedAlternative: entry.kind === "ResolutionTargetPublished",
    isCreationBaseline: proposalTimelineEntryIsCreationBaseline(entry),
  };
}

const commentTimelineKeyPrefix = "comment-";

/**
 * The message a comment entry came from, so a surface that owns comment behaviour — the Comments/Changes
 * pane — can render the entry as the comment itself, author's controls and all, rather than as a line of
 * history about it. Returns undefined for a change entry, which has no message behind it.
 */
export function proposalTimelineCommentMessageId(entry: ProposalTimelineEntryView): string | undefined {
  if (!entry.isComment || !entry.key.startsWith(commentTimelineKeyPrefix)) return undefined;
  return entry.key.slice(commentTimelineKeyPrefix.length) || undefined;
}

function proposalCommentToTimelineView(
  comment: ProposalTimelineComment,
  colorToken: ProposalTimelineColorToken,
): ProposalTimelineEntryView {
  return {
    sequence: 0,
    key: `${commentTimelineKeyPrefix}${comment.messageId}`,
    kind: "CommentAdded",
    label: "",
    fieldLabel: proposalTimelineFieldLabelForPath(comment.path),
    itemLabel: undefined,
    actionVerb: proposalTimelineActionVerb("CommentAdded"),
    isTeamChange: false,
    teamChange: undefined,
    isReviewerChange: false,
    reviewerChange: undefined,
    path: comment.path,
    itemId: undefined,
    sourceParty: comment.sourceParty,
    actorUserId: comment.authorUserId,
    actorLegalEntityId: comment.authorLegalEntityId,
    actorRole: null,
    // Ordering (and the displayed timestamp) uses the moment the comment became visible.
    createdDate: comment.publishedDate ?? comment.createdDate,
    valueSummary: comment.content,
    previousDisplayValue: undefined,
    nextDisplayValue: undefined,
    nextValue: undefined,
    valueIsStructured: false,
    sourceKey: proposalCommentTimelineSourceKey(comment),
    colorToken,
    isComment: true,
    isContentChange: false,
    isPublishedAlternative: false,
    isCreationBaseline: false,
  };
}

function proposalTimelineEntryKindIsComment(kind: ProposalNegotiationEventKind): boolean {
  return kind === "CommentAdded" || kind === "CommentThreadLinked";
}

function proposalCommentTimelineSourceKey(
  comment: Pick<ProposalTimelineComment, "sourceParty" | "authorLegalEntityId" | "authorUserId">,
): string {
  return [
    comment.sourceParty ?? "System",
    comment.authorLegalEntityId ?? "no-legal-entity",
    comment.authorUserId ?? "no-user",
  ].join(":");
}

function timelineViewTimestamp(view: ProposalTimelineEntryView): number {
  const parsed = Date.parse(view.createdDate);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Assigns color tokens by first (earliest-timestamp) appearance across the already-ordered merged list. */
function assignMergedTimelineColorTokens(
  views: readonly ProposalTimelineEntryView[],
): Record<string, ProposalTimelineColorToken> {
  const firstIndexBySource = new Map<string, number>();
  views.forEach((view, index) => {
    if (!firstIndexBySource.has(view.sourceKey)) firstIndexBySource.set(view.sourceKey, index);
  });
  return Object.fromEntries(
    [...firstIndexBySource.entries()]
      .sort(([, leftIndex], [, rightIndex]) => leftIndex - rightIndex)
      .map(([sourceKey], order) => [
        sourceKey,
        proposalTimelineColorTokens[order % proposalTimelineColorTokens.length],
      ]),
  );
}

export function summarizeProposalTimelineValue(
  entry: Pick<ProposalTimelineEntry, "valueExists" | "value" | "kind">,
): string | undefined {
  return summarizeProposalTimelineJsonValue(entry.valueExists, entry.value);
}

function summarizeProposalTimelineJsonValue(exists: boolean, value: unknown): string | undefined {
  if (!exists) return undefined;
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === undefined) return undefined;

  return JSON.stringify(value);
}

function proposalTimelineValueIsStructured(exists: boolean, value: unknown): boolean {
  return exists && value !== null && typeof value === "object";
}

/** The stored scalar itself, for callers that write it back rather than render it. */
function proposalTimelineScalarValue(exists: boolean, value: unknown): string | number | boolean | undefined {
  if (!exists) return undefined;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  return undefined;
}

/**
 * Scalar value for human display. Structured (object/array) values return undefined so the UI
 * renders an action phrase rather than dumping raw JSON — the exact regression Cluster D removes.
 * A date-field value renders as a local calendar date rather than its stored ISO datetime.
 */
function proposalTimelineDisplayValue(exists: boolean, value: unknown, path?: string | null): string | undefined {
  // A fee-schedule leaf is an object only because the column's value is wrapped in the column's own
  // name (`{ "feeRate": 125 }`); it is a scalar, and the shared vocabulary unwraps it. Rejecting it as
  // structured is what left a repriced or recoded category with nothing beside its label.
  if (exists && proposalFeeScheduleLeafPayload(path, value)) return proposalChangeValueText(path, value);
  if (proposalTimelineValueIsStructured(exists, value)) return undefined;
  // A CLEARED value is on the wire as `valueExists: true, value: null` — the optional-value encoding says
  // "there is a value here and it is nothing", which is how a removal is told apart from a field that was
  // never touched. To a reader that is an absence, and drawing it as the JSON literal "null" is worse than
  // drawing nothing: a cleared End Date read "28 October, 2026 → null". `proposalChangeValueText` has always
  // treated null as absence, so this is also what keeps the history and the save dialog in agreement.
  if (value === null) return undefined;
  if (!exists) return undefined;
  // All scalar display goes through the proposal's shared vocabulary. Besides booleans and dates, this
  // is important for percentages: the stored discount is a fraction, so rendering the number directly
  // made 5.5% appear as 0.055 in proposal history and resolution entries.
  return proposalChangeValueText(path, value);
}

/**
 * Maps a semantic negotiation path (e.g. "/responseDueBy", "/discount/percent") to the field's
 * human-readable display label. Descendant paths resolve by their leading segment so a
 * "/discount/percent" event still labels as "Discount".
 */
export function proposalTimelineFieldLabelForPath(path?: string | null): string | undefined {
  if (!path) return undefined;
  return proposalChangeFieldLabel(path);
}

/** Party-free verb describing what happened, for composing "{author} {verb} {field}". */
export function proposalTimelineActionVerb(kind: ProposalNegotiationEventKind, disposition?: unknown): string {
  switch (kind) {
    case "FieldSet":
    case "CollectionItemUpdated":
      return "updated";
    case "CollectionItemAdded":
      return "added";
    case "CollectionItemRemoved":
      return "removed";
    case "ResolutionTargetPublished":
      return "published an alternative";
    case "ResolutionDecisionRecorded":
      return proposalResolutionDecisionAction(disposition);
    case "CommentAdded":
      return "commented";
    case "CommentThreadLinked":
      return "linked a comment";
    case "ReviewCompleted":
      return "completed review";
    case "ReviewRequested":
      return "requested a re-review";
    case "SubmissionCreated":
      return "published proposal changes";
    case "ReviewerAssigned":
      return "assigned a reviewer";
    case "ReviewerRemoved":
      return "removed a reviewer";
    case "ReviewerAccessChanged":
      return "changed reviewer access";
    case "ReviewerMessageChanged":
      return "changed a reviewer message";
    case "ConflictDetected":
      return "produced a conflict";
    case "ConflictResolved":
      return "resolved a conflict";
    case "NegotiationCanceled":
      return "canceled the negotiation";
    case "ProposalHired":
      return "hired the proposal";
    case "ProposalAccepted":
      return "approved the proposal";
    case "ProposalRejected":
      return "rejected the proposal";
    case "RevisionRequested":
      return "requested a proposal revision";
    case "NegotiationStarted":
      return "started the negotiation";
    case "LegacyImported":
      return "imported legacy negotiation history";
    case "ParticipantAssociated":
      return "added a client team member";
    case "ParticipantRemoved":
      return "removed a client team member";
  }
}

/** Team-roster collection paths and the scalar team-leader field, per party. */
const teamMembersPathPattern = /^\/(?:client|vendor)Team\/members(?:\/|$)/;
const teamLeaderPathPattern = /^\/(?:client|vendor)Team\/leader(?:\/|$)/;

/** Discriminated team change, carrying both the previous and next member identity where they exist. */
export type ProposalTimelineTeamChangeKind =
  "leader-set" | "leader-cleared" | "leader-changed" | "member-added" | "member-removed" | "member-updated";

export type ProposalTimelineTeamChange = {
  kind: ProposalTimelineTeamChangeKind;
  /** userId of the outgoing individual (removed member, previous/cleared leader), when present. */
  previousUserId?: string;
  /** userId of the incoming individual (added member, new/set leader), when present. */
  nextUserId?: string;
};

/**
 * Describes a team-roster or team-leader change so the panel can render it descriptively — "Added Team
 * Member: X", "Removed Team Member: X", "Updated Team Leader: old → new" — with each individual shown by
 * name, avatar and profile link, instead of a generic "updated Team". Returns undefined for every
 * non-team path so other collections keep their existing phrasing. Both the outgoing and incoming member
 * identities are carried (a leader change and a member update both hold two).
 */
export function proposalTimelineTeamChange(
  entry: Pick<
    ProposalTimelineEntry,
    "kind" | "path" | "valueExists" | "value" | "expectedValueExists" | "expectedValue"
  >,
): ProposalTimelineTeamChange | undefined {
  const path = entry.path ?? "";
  const isLeader = teamLeaderPathPattern.test(path);
  const isMembers = teamMembersPathPattern.test(path);
  if (!isLeader && !isMembers) return undefined;

  const previousUserId = proposalTimelineEntityMemberUserId(entry.expectedValueExists, entry.expectedValue);
  const nextUserId = proposalTimelineEntityMemberUserId(entry.valueExists, entry.value);

  if (isLeader) {
    if (nextUserId && previousUserId) return { kind: "leader-changed", previousUserId, nextUserId };
    if (nextUserId) return { kind: "leader-set", nextUserId };
    return { kind: "leader-cleared", previousUserId };
  }

  switch (entry.kind) {
    case "CollectionItemRemoved":
      return { kind: "member-removed", previousUserId };
    case "CollectionItemUpdated":
      return { kind: "member-updated", previousUserId, nextUserId };
    default:
      return { kind: "member-added", nextUserId };
  }
}

function proposalTimelineEntityMemberUserId(exists: boolean, value: unknown): string | undefined {
  if (!exists || value === null || typeof value !== "object") return undefined;
  const userId = (value as { userId?: unknown }).userId;
  return typeof userId === "string" ? userId : undefined;
}

/**
 * Reviewer paths are "/{party}Reviewers/{userId}:{legalEntityId}", with "/isEditing" or
 * "/customMessage" trailing the two events that change one of those. Every reviewer event names its
 * reviewer in the path, which is the only place an access or message change carries the identity —
 * their values are a bare boolean and a bare string.
 */
const reviewerPathPattern = /^\/(?:client|vendor)Reviewers\/([^/:]+)/;

/** What a reviewer entry did, in reviewer-centric terms rather than the raw event kind. */
export type ProposalTimelineReviewerChangeKind =
  "reviewer-added" | "reviewer-removed" | "access-changed" | "message-changed";

export type ProposalTimelineReviewerChange = {
  kind: ProposalTimelineReviewerChangeKind;
  /** The reviewer the entry is about, named by the path segment that identifies them. */
  reviewerUserId?: string;
  /** Edit rights before the change; carried by an access change, which is the only entry that has one. */
  previousCanEdit?: boolean;
  /** Edit rights after the change, for an assignment and an access change alike. */
  canEdit?: boolean;
  /** True when a message change removed the reviewer's custom message rather than setting one. */
  messageCleared?: boolean;
};

/**
 * Describes a reviewer assignment, removal, access change or message change so the panel can render it
 * the way the save dialog already describes the same pending change — "Added Reviewer: Jane Doe (Full
 * edit)", "Updated Reviewer: Jane Doe (Comment only → Full edit)" — naming the reviewer and the rights
 * they ended up with. Returns undefined for every other event kind.
 */
export function proposalTimelineReviewerChange(
  entry: Pick<
    ProposalTimelineEntry,
    "kind" | "path" | "valueExists" | "value" | "expectedValueExists" | "expectedValue"
  >,
): ProposalTimelineReviewerChange | undefined {
  const reviewerUserId = reviewerPathPattern.exec(entry.path ?? "")?.[1];

  switch (entry.kind) {
    case "ReviewerAssigned":
      return {
        kind: "reviewer-added",
        reviewerUserId,
        canEdit: proposalTimelineReviewerSnapshotCanEdit(entry.valueExists, entry.value),
      };
    case "ReviewerRemoved":
      return {
        kind: "reviewer-removed",
        reviewerUserId,
        canEdit: proposalTimelineReviewerSnapshotCanEdit(entry.expectedValueExists, entry.expectedValue),
      };
    case "ReviewerAccessChanged":
      return {
        kind: "access-changed",
        reviewerUserId,
        previousCanEdit: proposalTimelineBooleanValue(entry.expectedValueExists, entry.expectedValue),
        canEdit: proposalTimelineBooleanValue(entry.valueExists, entry.value),
      };
    case "ReviewerMessageChanged":
      // A cleared message is on the wire as the JSON literal null, the same "there is a value here and
      // it is nothing" encoding every other removal uses.
      return {
        kind: "message-changed",
        reviewerUserId,
        messageCleared: !(entry.valueExists && typeof entry.value === "string" && entry.value.trim().length > 0),
      };
    default:
      return undefined;
  }
}

/** `isEditing` off an assignment/removal's reviewer snapshot, which is a whole reviewer object. */
function proposalTimelineReviewerSnapshotCanEdit(exists: boolean, value: unknown): boolean | undefined {
  if (!exists || value === null || typeof value !== "object") return undefined;
  const isEditing = (value as { isEditing?: unknown }).isEditing;
  return typeof isEditing === "boolean" ? isEditing : undefined;
}

function proposalTimelineBooleanValue(exists: boolean, value: unknown): boolean | undefined {
  return exists && typeof value === "boolean" ? value : undefined;
}

export function assignProposalTimelineColorTokens(
  entries: readonly ProposalTimelineEntry[],
): Record<string, ProposalTimelineColorToken> {
  const firstSequenceBySource = new Map<string, number>();
  for (const entry of entries) {
    const sourceKey = proposalTimelineSourceKey(entry);
    const existing = firstSequenceBySource.get(sourceKey);
    if (existing === undefined || entry.sequence < existing) firstSequenceBySource.set(sourceKey, entry.sequence);
  }

  return Object.fromEntries(
    [...firstSequenceBySource.entries()]
      .sort(([leftKey, leftSequence], [rightKey, rightSequence]) =>
        leftSequence === rightSequence ? leftKey.localeCompare(rightKey) : leftSequence - rightSequence,
      )
      .map(([sourceKey], index) => [
        sourceKey,
        proposalTimelineColorTokens[index % proposalTimelineColorTokens.length],
      ]),
  );
}

export function proposalTimelineSourceKey(
  entry: Pick<ProposalTimelineEntry, "sourceParty" | "actorUserId" | "actorLegalEntityId">,
): string {
  return [
    entry.sourceParty ?? "System",
    entry.actorLegalEntityId ?? "no-legal-entity",
    entry.actorUserId ?? "no-user",
  ].join(":");
}

export function describeProposalTimelineEntry(
  entry: Pick<ProposalTimelineEntry, "kind" | "sourceParty" | "actorRole" | "path" | "valueExists" | "value">,
): string {
  const party = proposalChangeActorLabel({
    actorRole: entry.actorRole,
    sourceParty: entry.sourceParty,
  });
  const target = entry.path ? ` ${entry.path}` : "";
  switch (entry.kind) {
    case "FieldSet":
      return `${party} updated${target}`;
    case "CollectionItemAdded":
      return `${party} added${target}`;
    case "CollectionItemUpdated":
      return `${party} updated${target}`;
    case "CollectionItemRemoved":
      return `${party} removed${target}`;
    case "ResolutionTargetPublished":
      return `${party} published an alternative${target}`;
    case "ResolutionDecisionRecorded":
      return `${party} ${proposalTimelineActionVerb(entry.kind, entry.valueExists ? entry.value : undefined)}${target}`;
    case "CommentAdded":
      return `${party} commented${target}`;
    case "CommentThreadLinked":
      return `${party} linked a comment${target}`;
    case "ReviewCompleted":
      return `${party} completed review`;
    case "ReviewRequested":
      return `${party} requested a re-review`;
    case "SubmissionCreated":
      return `${party} published proposal changes`;
    case "ReviewerAssigned":
      return `${party} assigned a reviewer`;
    case "ReviewerRemoved":
      return `${party} removed a reviewer`;
    case "ReviewerAccessChanged":
      return `${party} changed reviewer access`;
    case "ReviewerMessageChanged":
      return `${party} changed a reviewer message`;
    case "ConflictDetected":
      return `${party} produced a conflict${target}`;
    case "ConflictResolved":
      return `${party} resolved a conflict${target}`;
    case "NegotiationCanceled":
      return `${party} canceled the negotiation`;
    case "ProposalHired":
      return `${party} hired the proposal`;
    case "ProposalAccepted":
      return `${party} approved the proposal`;
    case "ProposalRejected":
      return `${party} rejected the proposal`;
    case "RevisionRequested":
      return `${party} requested a proposal revision`;
    case "NegotiationStarted":
      return `${party} started the negotiation`;
    case "LegacyImported":
      return `${party} imported legacy negotiation history`;
    case "ParticipantAssociated":
      return `${party} added a client team member`;
    case "ParticipantRemoved":
      return `${party} removed a client team member`;
  }
}

/**
 * Whether a raw negotiation event kind is a content change (a scalar field set or a collection
 * add/update/remove) — exported so callers over raw `ProposalTimelineEntry` data (which has no
 * `isContentChange` of its own; that field only exists on the derived `ProposalTimelineEntryView`) can
 * filter out comments/lifecycle events without duplicating the kind list.
 */
export function proposalTimelineEntryIsContentChange(kind: ProposalNegotiationEventKind): boolean {
  return (
    kind === "FieldSet" ||
    kind === "CollectionItemAdded" ||
    kind === "CollectionItemUpdated" ||
    kind === "CollectionItemRemoved"
  );
}
